import { useState } from "react";
import { router, useLocalSearchParams } from "expo-router";
import {
  ChevronRightIcon,
  Trash2Icon,
  TriangleAlertIcon,
} from "lucide-react-native";

import { CoverageSection } from "@/blocks/CoverageSection";
import { DocumentsSection } from "@/blocks/DocumentsSection";
import { LoadingScreen } from "@/blocks/LoadingScreen";
import { ScreenLayout } from "@/blocks/ScreenLayout";
import {
  VnAlertDialog,
  VnAlertDialogAction,
  VnAlertDialogCancel,
  VnAlertDialogContent,
  VnAlertDialogDescription,
  VnAlertDialogFooter,
  VnAlertDialogHeader,
  VnAlertDialogTitle,
} from "@/components/VnAlertDialog";
import { VnBox } from "@/components/VnBox";
import { VnButton } from "@/components/VnButton/VnButton";
import { VnCard } from "@/components/VnCard";
import { VnIcon } from "@/components/VnIcon";
import { VnLink } from "@/components/VnLink";
import { VnText } from "@/components/VnText/VnText";
import { useCheckCoverageMutation } from "@/data/mutations/coverageMutations";
import { useDeleteHouseholdMemberMutation } from "@/data/mutations/householdMembersMutations";
import {
  useCurrentHouseholdMemberQuery,
  useGetHouseholdMemberQuery,
} from "@/data/queries/householdMembersQueries";
import { useSheetToast } from "@/hooks/useSheetToast";
import { useTranslator } from "@/hooks/useTranslate";
import { envVariables } from "@/utils/envVariables";
import { formatHouseholdMemberName } from "@/utils/householdMember";

export const HouseholdMemberScreen = () => {
  const { householdMemberId } = useLocalSearchParams<{
    householdMemberId: string;
  }>();
  const { t } = useTranslator();
  const sheetToast = useSheetToast();

  const { data: currentUserHouseholdMember } = useCurrentHouseholdMemberQuery();

  const isPrimaryUser = currentUserHouseholdMember?.id === householdMemberId;
  const shouldShowDeleteOption = !isPrimaryUser;

  const {
    mutate: deleteHouseholdMember,
    isPending: isDeletingHouseholdMember,
  } = useDeleteHouseholdMemberMutation();

  const isLocalOrDevEnv =
    envVariables.EXPO_PUBLIC_ENVIRONMENT === "local" ||
    envVariables.EXPO_PUBLIC_ENVIRONMENT === "development";

  const { data: householdMember, isLoading } =
    useGetHouseholdMemberQuery(householdMemberId);

  const { mutateAsync: checkCoverage } = useCheckCoverageMutation();
  const [coverageStatus, setCoverageStatus] = useState<string | null>(null);
  const [householdMemberIdToDelete, setHouseholdMemberIdToDelete] = useState<
    string | null
  >(null);

  if (isLoading || !householdMember) {
    return <LoadingScreen />;
  }

  // Only used when isLocalOrDevEnv === true
  const handleCheckCoverage = () => {
    void checkCoverage(
      { householdMemberId },
      {
        onSuccess: data => {
          setCoverageStatus(data?.coverageStatus ?? t("common:error"));
        },
      },
    );
  };

  const handleDelete = () => {
    if (householdMemberIdToDelete) {
      deleteHouseholdMember(householdMemberIdToDelete, {
        onSuccess: () => {
          setHouseholdMemberIdToDelete(null);
          sheetToast.success(
            t("native:householdMemberScreen.deleteToast.success"),
          );
          router.back();
        },
        onError: () => {
          setHouseholdMemberIdToDelete(null);
          sheetToast.error(t("native:householdMemberScreen.deleteToast.error"));
        },
      });
    }
  };

  return (
    <ScreenLayout backgroundClassName="bg-background">
      <VnBox className="pt-safe-offset-4 web:py-4 flex w-full flex-1 gap-5">
        <VnText variant="headerTwo">
          {householdMember.firstName
            ? `${formatHouseholdMemberName(householdMember, "fullName")}`
            : t("common:householdMember")}
        </VnText>
        <VnLink
          href={{
            pathname:
              "/(authed)/household/[householdMemberId]/profile-information",
            params: { householdMemberId },
          }}>
          <VnCard className="flex-row items-center justify-between">
            <VnBox className="flex-1 gap-3">
              <VnText
                variant="lgMedium"
                i18nKey="native:householdMemberScreen.profileInformation"
              />
            </VnBox>
            <VnIcon
              as={ChevronRightIcon}
              size={20}
              className="text-foreground"
            />
          </VnCard>
        </VnLink>

        <CoverageSection householdMember={householdMember} />

        {isLocalOrDevEnv && (
          <VnCard className="items-start">
            <VnText
              variant="lgMedium"
              i18nKey="native:householdMemberScreen.checkCoverage"
            />
            <VnButton
              className="gap-4"
              i18nKey="native:householdMemberScreen.checkCoverage"
              onPress={handleCheckCoverage}
            />
            {coverageStatus && (
              <VnText
                variant="lg"
                className="text-muted-foreground"
                i18nKey="native:profileInformationScreen.coverageStatus"
                i18nOptions={{ status: coverageStatus }}
              />
            )}
          </VnCard>
        )}

        <VnCard className="gap-4">
          <VnText
            variant="lgMedium"
            i18nKey="native:householdMemberScreen.applicantDocuments"
          />
          <DocumentsSection
            householdMemberId={householdMemberId}
            householdMemberFullName={formatHouseholdMemberName(
              householdMember,
              "fullName",
            )}
            context="profile"
          />
        </VnCard>

        {shouldShowDeleteOption && (
          <VnButton
            variant="destructive"
            i18nKey="native:householdMemberScreen.deleteButton"
            i18nOptions={{
              memberFullName: formatHouseholdMemberName(
                householdMember,
                "fullName",
              ),
            }}
            IconComponent={Trash2Icon}
            isLoading={isDeletingHouseholdMember}
            iconProps={{
              className: "text-destructive",
            }}
            className="self-center"
            onPress={() => setHouseholdMemberIdToDelete(householdMemberId)}
          />
        )}
      </VnBox>

      <VnAlertDialog
        open={Boolean(householdMemberIdToDelete)}
        onOpenChange={isOpen => {
          if (!isOpen) {
            setHouseholdMemberIdToDelete(null);
          }
        }}>
        <VnAlertDialogContent>
          <VnAlertDialogHeader className="items-center gap-6">
            <VnBox className="bg-destructive/15 items-center self-center rounded-full p-4">
              <VnIcon
                as={TriangleAlertIcon}
                className="text-destructive"
                size={30}
              />
            </VnBox>
            <VnAlertDialogTitle i18nKey="native:householdMemberScreen.deleteDialog.title" />
            <VnAlertDialogDescription
              i18nKey="native:householdMemberScreen.deleteDialog.description"
              i18nOptions={{
                memberFullName: formatHouseholdMemberName(
                  householdMember,
                  "fullName",
                ),
              }}
            />
          </VnAlertDialogHeader>
          <VnAlertDialogFooter>
            <VnAlertDialogCancel i18nKey="common:cancel" />
            <VnAlertDialogAction
              variant="destructive"
              i18nKey="common:delete"
              IconComponent={Trash2Icon}
              iconProps={{
                className: "text-destructive",
              }}
              onPress={handleDelete}
            />
          </VnAlertDialogFooter>
        </VnAlertDialogContent>
      </VnAlertDialog>
    </ScreenLayout>
  );
};
